// Audit Methodology helpers — Integrated & Combined audit grouping (#17)
// Pure, presentational helpers. No backend contract change. Single Audit is the
// default and behaves exactly as before (no grouping).

export const METHODOLOGY_TYPES = {
  SINGLE: 'single',
  INTEGRATED: 'integrated',
  COMBINED: 'combined',
};

// Classify a free-text methodology label into a canonical type.
export const getMethodologyType = (methodology) => {
  const m = String(methodology || '').toLowerCase();
  if (m.includes('integrated')) return METHODOLOGY_TYPES.INTEGRATED;
  if (m.includes('combined')) return METHODOLOGY_TYPES.COMBINED;
  return METHODOLOGY_TYPES.SINGLE;
};

// True when the methodology groups multiple standards into one audit process.
export const isGroupedMethodology = (methodology) =>
  getMethodologyType(methodology) !== METHODOLOGY_TYPES.SINGLE;

// Scope labels are "Service / Standard / ServiceItem" — the standard is the
// middle segment. Any trailing " — Stage N" suffix on the service item is
// irrelevant to the standard name and left untouched.
export const extractStandardNameFromLabel = (label = '') => {
  const parts = String(label)
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts[1] : '';
};

// Ordered, de-duplicated list of member standard names across scopes.
export const collectMemberStandards = (scopes = []) => {
  const seen = new Set();
  const result = [];
  (Array.isArray(scopes) ? scopes : []).forEach((scope) => {
    const name = extractStandardNameFromLabel(scope?.label);
    if (name && name !== '-' && !seen.has(name)) {
      seen.add(name);
      result.push(name);
    }
  });
  return result;
};

// Compact human label with overflow handling so long groups don't overflow UI.
// e.g. ["ISO 9001","ISO 14001","ISO 45001","ISO 27001"] → "ISO 9001 + ISO 14001 + ISO 45001 +1 more"
export const formatMethodologyGroupLabel = (standards = [], maxVisible = 3) => {
  const list = Array.isArray(standards) ? standards.filter(Boolean) : [];
  if (list.length === 0) return '';
  if (list.length <= maxVisible) return list.join(' + ');
  const visible = list.slice(0, maxVisible);
  const rest = list.length - maxVisible;
  return `${visible.join(' + ')} +${rest} more`;
};

// Resolve member standard names, preferring the authoritative backend group
// (auditMethodologyGroup.memberStandards) and falling back to reconstructing
// them from scheduling scope labels when the group is absent (older API).
export const resolveMemberStandardNames = (methodologyGroup, fallbackScopes = []) => {
  const members = methodologyGroup && Array.isArray(methodologyGroup.memberStandards)
    ? methodologyGroup.memberStandards
    : [];
  const seen = new Set();
  const names = [];
  members.forEach((m) => {
    const name = m && m.standardName ? String(m.standardName).trim() : '';
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  });
  if (names.length > 0) return names;
  return collectMemberStandards(fallbackScopes);
};

// Output mode options for FAPP-01..08 forms under grouped methodologies.
export const OUTPUT_MODES = {
  COMBINED: 'combined',
  SPLIT: 'split',
};

const safeSlug = (value) => String(value || 'cycle')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '') || 'cycle';

const isInitialCycle = (value) => /\binitial\b/i.test(String(value || ''));

// CMF (Certificate Maintenance) and Fee line items (License Fee, Recertification Fee, etc.)
// are billing/maintenance rows, not audit cycles — mirrors the shouldHideScope filter already
// applied in buildSchedulingScopes / buildSchedulingScopesFromApiOptions. Those filters don't
// run for grouped methodology scopes (this function replaces their output wholesale from
// backend scopeGroups), so CMF/Fee must be filtered here too or they leak into AR/Scheduler
// dropdowns for Integrated/Combined audits. Checked even when the name also contains a cycle
// keyword (e.g. "Recertification Fee" contains "recert").
const isCmfScopeGroup = (scopeGroup, members) => {
  const names = [scopeGroup?.serviceItemName, ...members.map((member) => member?.serviceItemName)];
  return names.some((name) => {
    const text = String(name || '').toLowerCase();
    return text.includes('cmf') || text.includes('fee');
  });
};

const createGroupedScope = ({ methodologyType, scopeGroup, members, stageKey = null }) => {
  const primary = members[0] || {};
  const memberStandardNames = Array.from(new Set(members.map((member) => member?.standardName).filter(Boolean)));
  const cycleLabel = stageKey === 'stage1'
    ? `${scopeGroup.serviceItemName || 'Initial Certification'} — Stage 1 (Document Review)`
    : stageKey === 'stage2'
      ? `${scopeGroup.serviceItemName || 'Initial Certification'} — Stage 2 (Main Audit)`
      : (scopeGroup.serviceItemName || 'Audit Cycle');
  const baseKey = `mg-${methodologyType}-${safeSlug(scopeGroup.cycleKey || scopeGroup.serviceItemName)}-${primary.projectServiceStandardId || 'primary'}`;

  return {
    key: stageKey ? `${baseKey}:${stageKey === 'stage1' ? 's1' : 's2'}` : baseKey,
    methodologyScopeKey: stageKey ? `${baseKey}:${stageKey === 'stage1' ? 's1' : 's2'}` : baseKey,
    methodologyType,
    isMethodologyGroup: true,
    projectServiceId: primary.projectServiceId ?? scopeGroup.primaryProjectServiceId ?? null,
    projectServiceStandardId: primary.projectServiceStandardId ?? scopeGroup.primaryProjectServiceStandardId ?? null,
    standardId: primary.standardId ?? null,
    stageKey,
    auditDate: members.map((member) => member?.proposeDate).filter(Boolean).sort()[0] || null,
    label: `${methodologyType === METHODOLOGY_TYPES.INTEGRATED ? 'Integrated Audit' : 'Combined Audit'} / ${formatMethodologyGroupLabel(memberStandardNames)} / ${cycleLabel}`,
    memberScopes: members.map((member) => ({
      key: `${member.projectServiceId ?? 0}:${member.projectServiceStandardId ?? 0}${stageKey ? `:${stageKey === 'stage1' ? 's1' : 's2'}` : ''}`,
      ...member,
      stageKey,
    })),
    memberStandards: members.map((member) => ({
      standardId: member.standardId ?? null,
      standardName: member.standardName || '',
      projectServiceStandardId: member.projectServiceStandardId ?? null,
    })),
    includedProjectServiceStandardIds: Array.from(new Set(members.map((member) => member?.projectServiceStandardId).filter((id) => id != null))),
    includedStandardIds: Array.from(new Set(members.map((member) => member?.standardId).filter((id) => id != null))),
    includedServiceItemIds: Array.from(new Set(members.map((member) => member?.serviceItemId).filter((id) => id != null))),
  };
};

// Build stable, authoritative workflow scopes from backend scopeGroups.
// Single Audit and older API responses preserve the existing scope list.
//
// A methodology group covers only the standards the proposal actually put in it
// (`combinedStandardIds` / `integratedStandardIds`), which can be a SUBSET of the
// project's standards: check ISO 27001 + ISO 27701 on a project that also sells
// ISO 9001 and the backend emits scopeGroups for the first two only. The grouped
// scopes therefore REPLACE the cycles they cover and nothing else — every cycle
// row left outside the group stays in the list as its own single-audit cycle.
// Returning the grouped scopes alone dropped those standards from the Audit
// Process entirely: no cycle card, no Application Review scope, no scheduling —
// and so no audit task, no Technical Review and no certificate for a standard the
// client had bought.
export const buildMethodologySchedulingScopes = (methodologyGroup, fallbackScopes = []) => {
  if (!methodologyGroup?.grouped || !Array.isArray(methodologyGroup?.scopeGroups) || methodologyGroup.scopeGroups.length === 0) {
    return Array.isArray(fallbackScopes) ? fallbackScopes : [];
  }
  const methodologyType = methodologyGroup.methodologyType === METHODOLOGY_TYPES.COMBINED
    ? METHODOLOGY_TYPES.COMBINED
    : METHODOLOGY_TYPES.INTEGRATED;

  const groupedScopes = methodologyGroup.scopeGroups.flatMap((scopeGroup) => {
    const members = Array.isArray(scopeGroup?.members) ? scopeGroup.members.filter(Boolean) : [];
    if (members.length === 0) return [];
    if (isCmfScopeGroup(scopeGroup, members)) return [];
    if (isInitialCycle(scopeGroup?.serviceItemName)) {
      return [
        createGroupedScope({ methodologyType, scopeGroup, members, stageKey: 'stage1' }),
        createGroupedScope({ methodologyType, scopeGroup, members, stageKey: 'stage2' }),
      ];
    }
    return [createGroupedScope({ methodologyType, scopeGroup, members })];
  });

  // Matched on projectServiceStandardId, which identifies one CYCLE ROW of one
  // standard — not the standard as a whole. A member standard keeps any cycle that
  // the group did not absorb (e.g. a Surveillance the other members do not share),
  // and a CMF/fee group that was skipped above consumes nothing.
  const groupedStandardRowIds = new Set();
  groupedScopes.forEach((scope) => {
    (scope.memberScopes || []).forEach((member) => {
      if (member?.projectServiceStandardId != null) {
        groupedStandardRowIds.add(String(member.projectServiceStandardId));
      }
    });
  });
  const groupedKeys = new Set(groupedScopes.map((scope) => scope.key));

  const ungroupedScopes = (Array.isArray(fallbackScopes) ? fallbackScopes : []).filter((scope) => {
    if (!scope || groupedKeys.has(scope.key)) return false;
    // An unidentifiable row cannot be proven to be covered by the group, so it is
    // kept: a visible duplicate is recoverable, a silently unaudited standard is not.
    if (scope.projectServiceStandardId == null) return true;
    return !groupedStandardRowIds.has(String(scope.projectServiceStandardId));
  });

  return groupedScopes.concat(ungroupedScopes);
};

export const supportsMethodologyOutputMode = (documentCode) => /^FAPP-0[1-8]\b/i.test(String(documentCode || '').trim());

// Which grouped audits may actually CHOOSE their FAPP output shape.
//
// The certification rule differs by methodology:
//   Combined Audits   -> "there should be an audit report for each audit criteria",
//                        so per-standard output is the norm and a merged document is
//                        offered alongside it. The user gets the choice.
//   Integrated systems-> "there should be ONE integrated report produced after the
//                        audit". There is nothing to choose; split output is not a
//                        legal outcome.
// Accepts either a canonical METHODOLOGY_TYPES value or a raw methodology label.
export const supportsMethodologyOutputChoice = (documentCode, methodology) => (
  supportsMethodologyOutputMode(documentCode)
  && getMethodologyType(methodology) === METHODOLOGY_TYPES.COMBINED
);

export const buildMethodologyContext = ({ methodology, methodologyGroup, scopeContext, outputMode, projectId }) => {
  const methodologyType = getMethodologyType(methodology || methodologyGroup?.methodologyType);
  if (methodologyType === METHODOLOGY_TYPES.SINGLE || !scopeContext?.isMethodologyGroup) return null;
  // An Integrated audit produces ONE integrated report for all member standards,
  // so split output is forced back to combined here rather than trusted from the
  // caller. This is the authoritative enforcement point: the persisted
  // methodologyContext is what the document save/export branches read.
  const mode = (outputMode === OUTPUT_MODES.SPLIT && methodologyType !== METHODOLOGY_TYPES.INTEGRATED)
    ? OUTPUT_MODES.SPLIT
    : OUTPUT_MODES.COMBINED;
  return {
    methodologyType,
    methodologyScopeKey: scopeContext.methodologyScopeKey || scopeContext.key,
    cycleKey: scopeContext.stageKey || scopeContext.key,
    includedStandardIds: scopeContext.includedStandardIds || [],
    includedServiceItemIds: scopeContext.includedServiceItemIds || [],
    includedProjectServiceStandardIds: scopeContext.includedProjectServiceStandardIds || [],
    outputMode: mode,
    primaryStandardId: scopeContext.standardId || methodologyGroup?.primaryStandardId || null,
    memberStandards: scopeContext.memberStandards || methodologyGroup?.memberStandards || [],
    sourceProjectId: projectId == null ? null : Number(projectId),
  };
};

// ── Split output: one filled form per standard ─────────────────────────────
//
// A Combined Audit may produce "an audit report for each audit criteria", so its
// FAPP documents can be switched to `split` output. Split does not merely export
// the same answers N times: the operator fills the SAME form once per member
// standard, in tabs, and every standard keeps its own answers.
//
// Those complete per-standard forms live side by side under `standardForms`
// (keyed per standard) inside the ONE grouped-scope document — the same shape
// grouped FTRD-02 already uses for its certificate tabs (see
// certificateIssuanceTargets.js). Saving one standard's tab therefore can never
// overwrite another's, and the per-member-scope copies written on save carry
// each standard's own content instead of a clone of the first standard's.
export const SPLIT_STANDARD_FORMS_KEY = 'standardForms';

// Keyed by projectServiceStandardId — the same identity the member scopes and
// certificate targets use. standardId/name are only fallbacks for older payloads
// that never carried the project-service id.
const toSplitStandardKey = (member) => {
  const projectServiceStandardId = member?.projectServiceStandardId;
  if (projectServiceStandardId !== null && projectServiceStandardId !== undefined
    && String(projectServiceStandardId).trim() !== '') {
    return String(projectServiceStandardId);
  }
  const standardId = member?.standardId;
  if (standardId !== null && standardId !== undefined && String(standardId).trim() !== '') {
    return `std-${standardId}`;
  }
  const standardName = String(member?.standardName || '').trim();
  return standardName ? `name-${safeSlug(standardName)}` : '';
};

/** One tab per member standard of a grouped scope, in scope order, de-duplicated. */
export const resolveSplitStandardTargets = (scopeContext) => {
  const members = Array.isArray(scopeContext?.memberScopes) && scopeContext.memberScopes.length > 0
    ? scopeContext.memberScopes
    : (Array.isArray(scopeContext?.memberStandards) ? scopeContext.memberStandards : []);
  const seen = new Set();
  return members.reduce((targets, member) => {
    const standardKey = toSplitStandardKey(member);
    if (!standardKey || seen.has(standardKey)) return targets;
    seen.add(standardKey);
    return targets.concat({
      standardKey,
      scopeKey: member?.key || '',
      projectServiceStandardId: member?.projectServiceStandardId ?? null,
      standardId: member?.standardId ?? null,
      standardName: member?.standardName || '',
    });
  }, []);
};

/** The form as the operator edits it — without the per-standard container. */
export const stripSplitStandardForms = (form = {}) => {
  const { [SPLIT_STANDARD_FORMS_KEY]: _standardForms, ...rest } = form || {};
  return rest;
};

/** Reads the per-standard forms back out of a saved document, ignoring malformed data. */
export const readSplitStandardForms = (savedForm) => {
  const stored = savedForm?.[SPLIT_STANDARD_FORMS_KEY];
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
  return Object.entries(stored).reduce((result, [standardKey, form]) => (
    form && typeof form === 'object' && !Array.isArray(form)
      ? { ...result, [standardKey]: stripSplitStandardForms(form) }
      : result
  ), {});
};

/**
 * A standard's tab before anyone has filled it: the same form, seeded from the
 * shared content the group was carrying, scoped to that one standard.
 */
export const seedSplitStandardForm = (target, sourceForm = {}) => {
  const base = stripSplitStandardForms(sourceForm);
  const standardName = String(target?.standardName || '').trim();
  return {
    ...base,
    ...(target?.projectServiceStandardId == null
      ? {} : { projectServiceStandardId: target.projectServiceStandardId }),
    ...(target?.standardId == null ? {} : { standardId: target.standardId }),
    ...(standardName
      ? {
        standardName,
        // Mirrors how the form itself stores this field: an array stays an array,
        // a stored string stays a string.
        applicableStandards: (base.applicableStandards == null || Array.isArray(base.applicableStandards))
          ? [standardName]
          : standardName,
      }
      : {}),
  };
};

/** Every standard's form for the next save: the stored tabs plus the one being edited. */
export const buildSplitStandardFormMap = (
  targets = [],
  storedForms = {},
  activeStandardKey = '',
  activeForm = null,
) => (Array.isArray(targets) ? targets : []).reduce((result, target) => {
  const stored = storedForms?.[target.standardKey];
  const isActive = Boolean(activeStandardKey) && target.standardKey === activeStandardKey && activeForm;
  return {
    ...result,
    [target.standardKey]: isActive
      ? stripSplitStandardForms(activeForm)
      : (stored || seedSplitStandardForm(target, stored || {})),
  };
}, {});

/** What is written to the grouped document when a split-output form is saved. */
export const buildSplitPersistedForm = (activeForm = {}, standardFormMap = {}) => ({
  ...stripSplitStandardForms(activeForm),
  [SPLIT_STANDARD_FORMS_KEY]: standardFormMap,
});

/** The form that belongs to one member scope's own document copy. */
export const resolveSplitFormForMember = (
  member,
  targets = [],
  standardFormMap = {},
  fallbackForm = {},
) => {
  const target = (Array.isArray(targets) ? targets : []).find((candidate) => (
    (member?.key && candidate.scopeKey && String(candidate.scopeKey) === String(member.key))
    || (member?.projectServiceStandardId != null
      && candidate.projectServiceStandardId === member.projectServiceStandardId)
  ));
  if (!target) return stripSplitStandardForms(fallbackForm);
  return standardFormMap?.[target.standardKey] || seedSplitStandardForm(target, fallbackForm);
};
